import { useState } from "react";
import { Link, usePage } from "@inertiajs/react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Sticky top nav used across all marketing pages.
 * Reads `routes` and `auth` from Inertia shared props.
 */
export default function MarketingNav() {
  const { routes, auth } = usePage().props;
  const [open, setOpen] = useState(false);

  const close = () => setOpen(false);
  const authed = !!auth?.authenticated;
  const ctaLabel = authed ? dashboardLabel(routes.dashboard) : "Sign in";
  const ctaHref = authed ? routes.dashboard : routes.sign_in;

  return (
    <header className="border-b border-border/60 bg-background/80 backdrop-blur-sm sticky top-0 z-40">
      <div className="container flex h-16 items-center justify-between gap-3">
        <Link
          href={routes.home}
          className="font-display text-xl font-semibold text-foreground hover:text-primary transition-colors"
          onClick={close}
        >
          PagePledge<span className="text-primary">.</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link href={routes.how_it_works}>How it works</Link>
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link href={routes.pricing}>Pricing</Link>
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link href={routes.about_us}>About us</Link>
          </Button>
          <Button variant="ghost" size="sm" asChild>
            <Link href={routes.help}>Help</Link>
          </Button>
          {/* The demo opens in a new tab so the marketing site stays put. */}
          <Button variant="ghost" size="sm" asChild>
            <a href={routes.demo} target="_blank" rel="noopener">Try the demo</a>
          </Button>
          {authed ? (
            <Button size="sm" asChild>
              <Link href={routes.dashboard}>Dashboard</Link>
            </Button>
          ) : (
            <Button size="sm" asChild>
              <Link href={routes.sign_in}>Sign in</Link>
            </Button>
          )}
        </nav>

        {/* Mobile primary CTA + menu trigger */}
        <div className="md:hidden flex items-center gap-2">
          <Button size="sm" asChild>
            <Link href={ctaHref} onClick={close}>{ctaLabel}</Link>
          </Button>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {open && (
        <div className="md:hidden border-t border-border/60 bg-background">
          <nav className="container flex flex-col py-3 gap-1">
            <MobileLink href={routes.how_it_works} onClick={close}>How it works</MobileLink>
            <MobileLink href={routes.pricing} onClick={close}>Pricing</MobileLink>
            <MobileLink href={routes.about_us} onClick={close}>About us</MobileLink>
            <MobileLink href={routes.help} onClick={close}>Help</MobileLink>
            <MobileLink href={routes.demo} onClick={close} newTab>Try the demo</MobileLink>
          </nav>
        </div>
      )}
    </header>
  );
}

function MobileLink({ href, onClick, newTab = false, children }) {
  const className = "px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors";

  if (newTab) {
    return <a href={href} onClick={onClick} target="_blank" rel="noopener" className={className}>{children}</a>;
  }

  return (
    <Link href={href} onClick={onClick} className={className}>
      {children}
    </Link>
  );
}

function dashboardLabel(path) {
  if (!path) return "Dashboard";
  if (path.startsWith("/admin")) return "Admin";
  if (path.startsWith("/parents")) return "My children";
  if (path.startsWith("/schools/")) return "My school";
  return "Dashboard";
}
